import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { selectors } from 'state';
import { Button, Col, Row, Select, Space, Typography } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useReactToPrint } from 'react-to-print';
import { useTrainerAIReportData } from './aiReportShared';

const AiAnalysis: React.FC = () => {
  const { id } = useSelector(selectors.activeSession);
  const { promptsObj, responses } = useTrainerAIReportData(id);
  const players = useSelector(selectors.playersList);

  const [selectedPlayer, setSelectedPlayer] = useState<number | null>(null);
  const [selectedPrompt, setSelectedPrompt] = useState<number | null>(null);
  const [selectedResponse, setSelectedResponse] = useState<string>('');
  const printContentRef = useRef<HTMLDivElement | null>(null);

  const selectedPromptObj =
    selectedPrompt !== null ? promptsObj[selectedPrompt] : undefined;
  const selectedPlayerObj =
    selectedPlayer !== null
      ? players.find(player => player.id === selectedPlayer)
      : undefined;

  const handlePrint = useReactToPrint({
    content: () => printContentRef.current,
    documentTitle:
      selectedPromptObj && selectedPlayerObj
        ? `${selectedPromptObj.name} - ${selectedPlayerObj.name}`
        : 'ai-report',
    pageStyle: `
      @page {
        size: auto;
        margin: 16mm;
      }

      body {
        -webkit-print-color-adjust: exact;
      }
    `,
  });

  // When prompts arrive and none is selected yet, select the latest one
  useEffect(() => {
    const list = Object.values(promptsObj);

    if (list.length === 0) {
      // No prompts at all → clear selection & text
      setSelectedPrompt(null);
      setSelectedResponse('');
      return;
    }

    const stillExists =
      selectedPrompt != null && list.some(p => p.id === selectedPrompt);

    if (!stillExists) {
      // Either nothing was selected yet, or the selected prompt was deleted.
      // Pick the latest one.
      const latestPrompt = list[list.length - 1];
      setSelectedPrompt(latestPrompt.id);
    }
  }, [promptsObj, selectedPrompt]);

  useEffect(() => {
    if (players.length > 0 && selectedPlayer === null) {
      setSelectedPlayer(players[0].id);
    }
  }, [players, selectedPlayer]);

  useEffect(() => {
    if (selectedPrompt !== null && selectedPlayer !== null) {
      const response =
        Object.values(responses)
          .filter(
            response =>
              response.prompt === selectedPrompt &&
              response.player === selectedPlayer
          )
          .sort((a, b) => b.id - a.id)[0]?.response || '';
      setSelectedResponse(response);
    }
  }, [selectedPrompt, selectedPlayer, responses]);

  const promptOptions = useMemo(
    () => Object.values(promptsObj).sort((a, b) => b.id - a.id),
    [promptsObj]
  );

  return (
    <div>
      <Row>
        <Space>
          <Select<number>
            showSearch
            allowClear
            placeholder="Player 1"
            optionFilterProp="children"
            style={{ width: 180 }}
            value={selectedPlayer ?? undefined}
            onChange={value => setSelectedPlayer(value ?? null)}
          >
            {players.map(player => (
              <Select.Option key={player.id} value={player.id}>
                {player.name}
              </Select.Option>
            ))}
          </Select>
          <Select<number>
            showSearch
            allowClear
            placeholder="Prompt"
            optionFilterProp="children"
            style={{ width: 240 }}
            value={selectedPrompt ?? undefined}
            onChange={value => setSelectedPrompt(value ?? null)}
          >
            {promptOptions.map(prompt => (
              <Select.Option key={prompt.id} value={prompt.id}>
                {prompt.name}
              </Select.Option>
            ))}
          </Select>
          <Button
            onClick={handlePrint}
            disabled={!selectedResponse}
            icon={<DownloadOutlined />}
          >
            Download PDF
          </Button>
        </Space>
      </Row>

      <Row gutter={10}>
        <Col>
          <div
            style={{
              height: '70vh',
              marginTop: 10,
              width: 700,
              padding: 12,
              border: '1px solid #d9d9d9',
              borderRadius: 4,
              overflowY: 'auto',
              background: '#fff',
            }}
          >
            <div ref={printContentRef}>
              <Typography.Title level={3}>
                {selectedPromptObj?.name || 'AI Report'}
              </Typography.Title>
              <Typography.Paragraph type="secondary">
                Player: {selectedPlayerObj?.name || '-'}
              </Typography.Paragraph>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {selectedResponse}
              </ReactMarkdown>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default AiAnalysis;
